import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import Box from "@material-ui/core/Box";
import jwtDecode from "jwt-decode";

import ViewPicker from "./components/ViewPicker";
import FilterBar from "./components/FilterBar";
// import QuickSummary from "./components/QuickSummary";
import SearchActionBar from "./components/SearchActionBar";
import ShipmentsTable from "./components/ShipmentsTable";
import ShipmentMessagesDialog from "./components/ShipmentMessagesDialog";
import CommentsDialog from "./components/CommentsDialog";
import ShipmentDetailsPage from "./components/ShipmentDetailsPage";
import Pagination from "./components/Pagination";
import ResetBatchDialog from "../sasl/batchesDetails/ResetBatchDialog";
import CommonDigitizedDocument from "./components/CommonDigitizedDocument";
import ShipmentScheduleDialog from "./components/ShipmentScheduleDialog";
import ShipmentScheduleIDForm from "./components/ShipmentScheduleIDForm";
import GenerateProformaDialog from "./components/GenerateProformaDialog";
import GenerateConsigneeGRNDialog from "./components/GenerateConsigneeGRNDialog";
import SyncShipmentStatusDialog from "./components/SyncShipmentStatusDialog";

import {
  VIEWS,
  // STATUSES,
  STATUSES_BY_VIEW,
  TILE_TO_LIST_OF_ACTIONS,
  SYNC_TRANSITIONS,
} from "./helpers/statusConfig";
import { appUrl } from "../../../../../utils/config";
import {
  fetchAllDashboardFilters,
  saveDashboardFilters,
} from "../../../containers/utils/filterUtils";
import {
  MOCK_LOGISTICS,
  MOCK_PRE_SCHEDULE_RESPONSE,
  MOCK_SHIPMENT_RESPONSE,
  MOCK_SHIPMENT_CONTAINER_BATCHES,
} from "./helpers/mockData";

const DASHBOARD_TYPE = "logisticsCollaborationDashboard";

const useStyles = makeStyles(() => ({
  page: {
    padding: "6px 14px 24px 14px",
    backgroundColor: "#FFFFFF",
    minHeight: "100%",
  },
}));

const DEFAULT_FILTERS = {
  cmos: [],
  consignees: [],
  freightForwarders: [],
  brands: [],
  sponsors: [],
};

const getApiBase = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

const LogisticsCollaborationDashboard = () => {
  // const classes = useStyles();
  const role =
    (typeof localStorage !== "undefined" && localStorage.collabType) ||
    "PharmaCo";
  const isCMO = role === "CMO";

  const [view, setView] = useState(VIEWS.PRE_SCHEDULE);

  const [filterOptions, setFilterOptions] = useState({
    cmos: [],
    consignees: [],
    freightForwarders: [],
    brands: [],
    sponsors: [],
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  // committedFilters is what the API uses; updated only when a filter dropdown closes
  const [committedFilters, setCommittedFilters] = useState(DEFAULT_FILTERS);
  const pendingFiltersRef = useRef(DEFAULT_FILTERS);
  // prevents the initial data fetch from firing before loadSavedFilters resolves
  const [filtersReady, setFiltersReady] = useState(false);
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  // searchCriteria + searchValue = live input; submittedSearch = committed to API
  const [searchCriteria, setSearchCriteria] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState({
    field: "all",
    value: "",
  });

  const [listOfActions, setListOfActions] = useState("all");
  const [quickSummaryKey, setQuickSummaryKey] = useState(null);
  const [shipmentScheduleDialog, setShipmentScheduleDialog] = useState({
    open: false,
    row: null,
  });
  const [shipmentScheduleForm, setShipmentScheduleForm] = useState({
    open: false,
    batches: [],
    row: null,
  });
  // const [quickSummaryCounts, setQuickSummaryCounts] = useState({});
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [shipmentsError, setShipmentsError] = useState(false);
  // const [quickSummaryLoading, setQuickSummaryLoading] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [messagesDrawer, setMessagesDrawer] = useState({
    open: false,
    shipment: null,
    batchesList: [],
    batchesError: false,
  });
  const [commentsDialog, setCommentsDialog] = useState({
    open: false,
    shipment: null,
  });
  const [viewShipmentDetailsPage, setViewShipmentDetailsPage] = useState({
    open: false,
    row: null,
  });
  const [resetBatchDialog, setResetBatchDialog] = useState({ open: false });
  const [reviewDocumentRow, setReviewDocumentRow] = useState(null);
  const [generateProformaRow, setGenerateProformaRow] = useState(null);
  const [generateConsigneeGRNRow, setGenerateConsigneeGRNRow] = useState(null);
  const [syncStatusRow, setSyncStatusRow] = useState(null);
  const [shipmentStatuses, setShipmentStatuses] = useState([]);

  // Client-side status advances applied after a successful "Sync Status" ingest
  // (e.g. 63 Shipment Scheduled → 64 Shipped). Keyed by shipment ID and applied to
  // every fetched row so the new status survives view/filter refreshes — the actual
  // ledger write happens on the backend. A ref (not state) keeps fetchData reading
  // the latest map without needing it in the effect's dependency list.
  const statusOverridesRef = useRef({});

  const shipmentKey = (r) => r.sponsorShipmentID || r.collabrixFGBatchShipmentID;

  // Apply post-ingest status advances to the fetched rows for `currentView`. A row
  // whose advance moved it OUT of the current view (e.g. Scheduled→Shipped leaves
  // Post-Schedule) is dropped; one that stays (e.g. Shipped→Goods Received in
  // Post-Shipment) is updated in place. Non-overridden rows pass through untouched.
  const applyStatusOverrides = (rows, currentView) => {
    const allowed = STATUSES_BY_VIEW[currentView];
    return rows.reduce((acc, r) => {
      const key = shipmentKey(r);
      const ov = key != null ? statusOverridesRef.current[key] : null;
      if (!ov) {
        acc.push(r);
        return acc;
      }
      const merged = { ...r, statusID: ov.statusID, statusDescription: ov.statusDescription };
      if (allowed && !allowed.includes(merged.statusID)) return acc; // advanced into another view
      acc.push(merged);
      return acc;
    }, []);
  };

  // Load filter dropdown options + saved filters on mount
  useEffect(() => {
    fetchFilterOptions();
    loadSavedFilters();
    fetchFGStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quick summary: re-fetch only when filters change (not on view/page/search changes)
  // useEffect(() => {
  //   if (!filtersReady) return;
  //   fetchQuickSummary();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [filtersReady, committedFilters]);

  // Table data: re-fetch whenever any relevant param changes
  useEffect(() => {
    if (!filtersReady) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersReady,
    view,
    committedFilters,
    submittedSearch,
    listOfActions,
    quickSummaryKey,
    page,
    perPage,
    dataRefreshKey,
  ]);

  // ---- Filter option loaders

  const fetchFGStatuses = async () => {
    try {
      const res = await axios.get(`${getApiBase()}/fetchFGStatuses/true`);
      setShipmentStatuses(res.data?.data?.shipmentStatuses || []);
    } catch (err) {
      console.error("[Logistics] Failed to fetch FG statuses", err);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const base = getApiBase();
      const tokenData = jwtDecode(localStorage.getItem("authToken") || "");
      const [consigneesRes, ffRes, brandsRes, mfgPlantsRes, sponsorsRes] =
        await Promise.allSettled([
          axios.get(`${base}/fetchUniqueConsignees`),
          axios.get(`${base}/fetchFreightForwarderDetails`),
          axios.get(`${base}/fetchBrandValues`),
          axios.get(`${base}/fetchMfgPlants`),
          isCMO
            ? axios.post(`${appUrl}/sml/fetchSponsorsForCMO`, {
                cmoOrgID: tokenData?.organization_id,
              })
            : Promise.resolve(null),
        ]);
      const val = (res) => (res.status === "fulfilled" ? res.value : null);
      setFilterOptions((prev) => ({
        ...prev,
        cmos: val(mfgPlantsRes)?.data?.MfgPlantsDetails || [],
        consignees: (val(consigneesRes)?.data?.consigneeList || []).filter((c) => c.deliveryAddresseeOrgName != null),
        freightForwarders: val(ffRes)?.data?.freightForwardersList || [],
        brands: (val(brandsRes)?.data?.data || []).map((b) => b.brand || b),
        sponsors: val(sponsorsRes)?.data?.data || [],
      }));
    } catch (err) {
      console.error("[Logistics] Failed to load filter options", err);
    }
  };

  const loadSavedFilters = async () => {
    try {
      const result = await fetchAllDashboardFilters();
      const saved = result?.data?.[DASHBOARD_TYPE];
      if (saved) {
        const savedFilters = {
          cmos: saved.cmos || [],
          consignees: saved.consignees || [],
          freightForwarders: saved.freightForwarders || [],
          brands: saved.brands || [],
          sponsors: saved.sponsors || [],
        };
        pendingFiltersRef.current = savedFilters;
        setFilters(savedFilters);
        setCommittedFilters(savedFilters);
      }
    } catch (err) {
      console.error("[Logistics] Failed to load saved filters", err);
    } finally {
      setFiltersReady(true);
    }
  };

  // ---- Data fetchers

  // const fetchQuickSummary = async () => {
  //   // setQuickSummaryLoading(true);
  //   try {
  //     const tokenData = jwtDecode(localStorage.getItem("authToken") || "");
  //     const res = await axios.post(
  //       `${getApiBase()}/fetchLogisticsDashboardQuickSummary`,
  //       {
  //         aslNumberList: committedFilters.cmos,
  //         consigneeList: committedFilters.consignees,
  //         freightForwardersList: committedFilters.freightForwarders,
  //         brand: committedFilters.brands,
  //         collaboratorTypeID: tokenData?.collaborator_type_id,
  //         brandOwnerMosymphonyIDs: committedFilters?.sponsors,
  //         vendorMosymphonyID: isCMO ? tokenData?.organization_id : null,
  //       },
  //     );
  //     const d = res.data?.data || {};
  //     setQuickSummaryCounts({
  //       marketPOsReady: d.readyForDispatchPOsCount,
  //       marketPOsScheduled: d.shipmentScheduledPOsCount,
  //       marketPOsInTransit: d.shipmentInTransitPOsCount,
  //       marketPOsDelivered: d.shipmentDeliveredPOsCount,
  //       newActions: d.deliveriesWithNewActionCount,
  //       completed: d.deliveriesCompletedCount,
  //       delayed: d.delayedDeliveriesCount,
  //     });
  //   } catch (err) {
  //     console.error("[Logistics] Failed to fetch quick summary", err);
  //   } finally {
  //     setQuickSummaryLoading(false);
  //   }
  // };

  const fetchData = async () => {
    setShipmentsLoading(true);

    try {
      let rows = [];
      let totalRecords = 0;

      if (MOCK_LOGISTICS) {
        let allRows = [];
        if (view === VIEWS.PRE_SCHEDULE) {
          const data = MOCK_PRE_SCHEDULE_RESPONSE.data;
          allRows = Array.isArray(data.batchesList) ? data.batchesList : [];
        } else {
          const viewKey = view === VIEWS.POST_SCHEDULE ? "postSchedule" : "postDelivery";
          const data = MOCK_SHIPMENT_RESPONSE[viewKey].data;
          allRows = Array.isArray(data.shipmentList) ? data.shipmentList : [];
        }
        totalRecords = allRows.length;
        rows = allRows.slice((page - 1) * perPage, page * perPage);
      } else {
        const tokenData = jwtDecode(localStorage.getItem("authToken") || "");
        const effectiveListOfActions = quickSummaryKey
          ? TILE_TO_LIST_OF_ACTIONS[quickSummaryKey] || "all"
          : listOfActions;

        const basePayload = {
          searchField: submittedSearch.field !== "all" ? submittedSearch.field : "all",
          searchValue: submittedSearch.value || "",
          listOfActions: effectiveListOfActions === 'all' ? []:[effectiveListOfActions],
          aslNumberList: committedFilters.cmos,
          consigneeList: committedFilters.consignees,
          freightForwardersList: committedFilters.freightForwarders,
          brand: committedFilters.brands,
          collaboratorTypeID: tokenData?.collaborator_type_id,
          brandOwnerMosymphonyIDs: committedFilters?.sponsors,
          pageNumber: page,
          pageSize: perPage,
        };

        if (view === VIEWS.PRE_SCHEDULE) {
          const res = await axios.post(
            `${getApiBase()}/fetchDataForPreScheduleView`,
            basePayload,
          );
          const data = res?.data?.data || {};
          rows = Array.isArray(data.batchesList) ? data.batchesList : [];
          totalRecords = data.totalNoOfRecords || rows.length;
        } else {
          const res = await axios.post(
            `${getApiBase()}/fetchShipmentDataForLogistics`,
            { ...basePayload, selectedDashboardView: view === VIEWS.POST_SCHEDULE ? 'postShipment' : view },
          );
          const data = res?.data?.data || {};
          rows = Array.isArray(data.shipmentList) ? data.shipmentList : [];
          totalRecords = data.totalNoOfRecords || rows.length;
        }
      }

      // Re-apply any post-ingest status advances (see statusOverridesRef), dropping
      // rows that the advance moved into another view.
      setShipments(applyStatusOverrides(rows, view));
      setTotal(totalRecords);
      setLastSyncedAt(new Date());
      setShipmentsError(false);
    } catch (err) {
      console.error("[Logistics] Failed to fetch data", err);
      setShipments([]);
      setTotal(0);
      setShipmentsError(true);
    } finally {
      setShipmentsLoading(false);
    }
  };

  // ---- Handlers

  const handleFilterChange = (key, value) => {
    const next = { ...pendingFiltersRef.current, [key]: value };
    pendingFiltersRef.current = next;
    setFilters(next);
  };

  // Called when a filter dropdown closes — commits pending selections to the API
  const handleFilterCommit = () => {
    setCommittedFilters(pendingFiltersRef.current);
    setPage(1);
  };

  const handleClearAllFilters = () => {
    pendingFiltersRef.current = DEFAULT_FILTERS;
    setFilters(DEFAULT_FILTERS);
    setCommittedFilters(DEFAULT_FILTERS);
    setQuickSummaryKey(null);
    setSearchCriteria("all");
    setSearchValue("");
    setSubmittedSearch({ field: "all", value: "" });
    setListOfActions("all");
    setPage(1);
  };

  const handleRowClick = (row) => {
    if (row.statusID === 62 && localStorage?.collabType !== "CMO") {
      setShipmentScheduleDialog({ open: true, row });
    }
  };

  const handleScheduleProceed = (batches, meta) => {
    setShipmentScheduleDialog({ open: false, row: null });
    setShipmentScheduleForm({
      open: true,
      batches,
      meta,
      row: shipmentScheduleDialog.row,
    });
  };

  const handleSaveFilter = async () => {
    try {
      await saveDashboardFilters(DASHBOARD_TYPE, filters);
    } catch (err) {
      console.error("[Logistics] Failed to save filters", err);
    }
  };

  const handleViewChange = (v) => {
    setView(v);
    setPage(1);
  };

  // const handleQuickSummarySelect = (key) => {
  //   setQuickSummaryKey((curr) => (curr === key ? null : key));
  //   setListOfActions("all");
  //   setPage(1);
  // };

  const handleSearchSubmit = () => {
    setSubmittedSearch({ field: searchCriteria, value: searchValue });
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchCriteria("all");
    setSearchValue("");
    setSubmittedSearch({ field: "all", value: "" });
    setPage(1);
  };

  const handleAction = (row, action) => {
    switch (action) {
      case "SCHEDULE_SHIPMENT":
        if (localStorage?.collabType !== "CMO") {
          setShipmentScheduleDialog({ open: true, row });
        }
        break;
      case "GENERATE_CONSIGNEE_GRN":
        setGenerateConsigneeGRNRow(row);
        break;
      case "GENERATE_PROFORMA":
        setGenerateProformaRow(row);
        break;
      case "SYNC_STATUS":
        setSyncStatusRow(row);
        break;
      case "REVIEW_DOCUMENT":
        setReviewDocumentRow(row);
        break;
      case "VIEW_SHIPMENT_DETAILS":
        setViewShipmentDetailsPage({ open: true, row });
        break;
      case "RESET_BATCH":
        setResetBatchDialog({ open: true });
        break;
      default:
        // eslint-disable-next-line no-console
        console.log("[Logistics] Action", action, "on row", row);
    }
  };

  const handleOpenMessages = (row) => {
    // TODO: Uncomment when API is ready
    axios
      .post(`${getApiBase()}/fetchBatchesForShipmentIDGeneration`, {
        aslNumber: null,
        temperature: null,
        deliveryAddresseeOrgName: null,
        sponsorShipmentID: row.sponsorShipmentID,
      })
      .then((res) => {
        setMessagesDrawer({
          open: true,
          shipment: row,
          batchesList: res.data.data.batchesList || [],
          batchesError: false,
        });
      })
      .catch(() => {
        setMessagesDrawer({
          open: true,
          shipment: row,
          batchesList: [],
          batchesError: true,
        });
      });
    // setMessagesDrawer({ open: true, shipment: row, batchesList: MOCK_SHIPMENT_CONTAINER_BATCHES });
  };

  const handleComment = (row) => {
    setCommentsDialog({ open: true, shipment: row, thread: [] });
  };
  if (shipmentScheduleForm.open) {
    return (
      <ShipmentScheduleIDForm
        row={shipmentScheduleForm.row}
        batches={shipmentScheduleForm.batches}
        onBack={() => {
          setShipmentScheduleForm({ open: false, batches: [], row: null });
          setDataRefreshKey((k) => k + 1);
        }}
      />
    );
  }

  return (
    <div >
      <FilterBar
        role={role}
        filterOptions={filterOptions}
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterCommit={handleFilterCommit}
        onSaveFilter={handleSaveFilter}
        onClearAll={handleClearAllFilters}
        rightSlot={
          <ViewPicker value={view} onChange={handleViewChange} role={role} />
        }
      />

      {/* <QuickSummary
        counts={quickSummaryCounts}
        loading={quickSummaryLoading}
        selectedKey={quickSummaryKey}
        onSelect={handleQuickSummarySelect}
        onClear={() => {
          setQuickSummaryKey(null);
          setPage(1);
        }}
      /> */}
      <hr />

      <SearchActionBar
        searchCriteria={searchCriteria}
        searchValue={searchValue}
        onSearchCriteriaChange={setSearchCriteria}
        onSearchValueChange={setSearchValue}
        onSearchSubmit={handleSearchSubmit}
        onClearSearch={handleClearSearch}
        shipmentStatuses={shipmentStatuses}
        listOfActions={listOfActions}
        onListOfActionsChange={(v) => {
          setListOfActions(v);
          setQuickSummaryKey(null);
          setPage(1);
        }}
        perPage={perPage}
        onPerPageChange={(v) => {
          setPerPage(v);
          setPage(1);
        }}
        lastSyncedAt={lastSyncedAt}
        onRefresh={fetchData}
        view={view}
      />

      <ShipmentsTable
        key={view}
        view={view}
        rows={shipments}
        loading={shipmentsLoading}
        error={shipmentsError}
        role={role}
        onAction={handleAction}
        onComment={handleComment}
        onOpenMessages={handleOpenMessages}
        onRowClick={handleRowClick}
      />

      <Pagination
        page={page}
        totalPages={Math.max(1, Math.ceil(total / perPage))}
        onChange={setPage}
      />

      <ShipmentMessagesDialog
        open={messagesDrawer.open}
        onClose={() =>
          setMessagesDrawer({
            open: false,
            shipment: null,
            batchesList: [],
            batchesError: false,
          })
        }
        shipment={messagesDrawer.shipment}
        batchesList={messagesDrawer.batchesList}
        batchesError={messagesDrawer.batchesError}
      />

      <ShipmentScheduleDialog
        open={shipmentScheduleDialog.open}
        row={shipmentScheduleDialog.row}
        onClose={() => {
          setShipmentScheduleDialog({ open: false, row: null });
          fetchData();
        }}
        onProceed={handleScheduleProceed}
      />

      <CommentsDialog
        open={commentsDialog.open}
        onClose={() => setCommentsDialog({ open: false, shipment: null })}
        shipment={commentsDialog.shipment}
        viewType={view}
      />

      <Dialog
        open={viewShipmentDetailsPage.open && !!viewShipmentDetailsPage.row}
        fullScreen
      >
        {viewShipmentDetailsPage.open && viewShipmentDetailsPage.row && (
          <ShipmentDetailsPage
            row={viewShipmentDetailsPage.row}
            onBack={() => setViewShipmentDetailsPage({ open: false, row: null })}
          />
        )}
      </Dialog>

      <GenerateProformaDialog
        open={Boolean(generateProformaRow)}
        row={generateProformaRow}
        onClose={() => {
          setGenerateProformaRow(null);
          fetchData();
        }}
      />

      <GenerateConsigneeGRNDialog
        open={Boolean(generateConsigneeGRNRow)}
        row={generateConsigneeGRNRow}
        onClose={() => {
          setGenerateConsigneeGRNRow(null);
          fetchData();
        }}
      />

      <SyncShipmentStatusDialog
        open={Boolean(syncStatusRow)}
        row={syncStatusRow}
        onClose={(synced) => {
          // synced === true only after the document was ingested (success screen).
          // Record the one-step advance so the table reflects the new status.
          if (synced && syncStatusRow) {
            const t = SYNC_TRANSITIONS[syncStatusRow.statusID || syncStatusRow.statusId];
            const key = shipmentKey(syncStatusRow);
            if (t && key != null) {
              statusOverridesRef.current = {
                ...statusOverridesRef.current,
                [key]: { statusID: t.toStatusId, statusDescription: t.toLabel },
              };
            }
          }
          setSyncStatusRow(null);
          fetchData();
        }}
      />

      <ResetBatchDialog
        open={resetBatchDialog.open}
        onClose={() => setResetBatchDialog({ open: false })}
        onContinue={(data) => {
          // eslint-disable-next-line no-console
          console.log("[Logistics] Reset batch continue", data);
          setResetBatchDialog({ open: false });
        }}
      />

      <Dialog
        open={Boolean(reviewDocumentRow)}
        onClose={() => setReviewDocumentRow(null)}
        fullScreen
      >
        {reviewDocumentRow &&
          (() => {
            const file =
              Array.isArray(reviewDocumentRow.customFilesList) &&
              reviewDocumentRow.customFilesList.length > 0
                ? reviewDocumentRow.customFilesList[0]
                : null;
            return (
              <Box
                style={{
                  padding: "24px",
                  height: "100%",
                  boxSizing: "border-box",
                  overflowY: "auto",
                }}
              >
                <CommonDigitizedDocument
                  initialData={reviewDocumentRow.kvpJson || {}}
                  digitizedData={
                    reviewDocumentRow.kvpJson
                      ? {
                          ...reviewDocumentRow.kvpJson,
                          documentType: reviewDocumentRow.kvpJson.document_type,
                        }
                      : null
                  }
                  batchDetails={reviewDocumentRow}
                  poNumber={reviewDocumentRow.scheduleAgreementNumber}
                  batchNumber={reviewDocumentRow.vendorBatchNumber}
                  schrockenCustomFileID={
                    file ? file.schrockenCustomFileID : null
                  }
                  onClose={() => setReviewDocumentRow(null)}
                />
              </Box>
            );
          })()}
      </Dialog>
    </div>
  );
};

export default LogisticsCollaborationDashboard;
